/**
 * Route corridor parsing and equivalence helpers.
 * Orders may store corridorClass values (e.g. gauteng_kzn) while trips use
 * registry codes (e.g. GP-KZN). These helpers align both formats for matching.
 */

const PROVINCE_CODES = new Set(["GP", "WC", "EC", "KZN", "LP", "MP", "NW", "NC", "FS"]);

const PROVINCE_ALIASES = {
  gauteng: "GP",
  gp: "GP",
  "western cape": "WC",
  western_cape: "WC",
  wc: "WC",
  "eastern cape": "EC",
  eastern_cape: "EC",
  ec: "EC",
  kzn: "KZN",
  "kwazulu-natal": "KZN",
  kwazulu_natal: "KZN",
  kwazulunatal: "KZN",
  limpopo: "LP",
  lp: "LP",
  mpumalanga: "MP",
  mp: "MP",
  "north west": "NW",
  north_west: "NW",
  nw: "NW",
  "northern cape": "NC",
  northern_cape: "NC",
  nc: "NC",
  "free state": "FS",
  free_state: "FS",
  fs: "FS",
};

/**
 * @param {string|null|undefined} token
 * @returns {string|null}
 */
export function normalizeProvinceToken(token) {
  if (!token || typeof token !== "string") return null;

  const trimmed = token.trim();
  const upper = trimmed.toUpperCase();
  if (PROVINCE_CODES.has(upper)) {
    return upper;
  }

  const normalized = trimmed
    .toLowerCase()
    .replace(/[-–—]/g, " ")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (PROVINCE_ALIASES[normalized]) {
    return PROVINCE_ALIASES[normalized];
  }

  for (const [alias, code] of Object.entries(PROVINCE_ALIASES)) {
    if (normalized.includes(alias) || alias.includes(normalized)) {
      return code;
    }
  }

  return null;
}

/**
 * Extract a province code from a location object or address string.
 * Prefers structured province fields, then comma-separated address parts
 * from the end (so "1 Vrede Avenue, Bryanston, Gauteng" → GP).
 * @param {Object|string|null|undefined} location
 * @returns {string|null}
 */
export function extractProvinceFromLocation(location) {
  if (!location) return null;
  if (typeof location === "string") {
    return extractProvinceFromAddressText(location);
  }

  const fromField = normalizeProvinceToken(
    location.addressComponents?.province ||
      location.province ||
      location.state ||
      location.addressComponents?.state
  );
  if (fromField) return fromField;

  return extractProvinceFromAddressText(
    [location.address, location.formattedAddress].filter(Boolean).join(", ")
  );
}

function extractProvinceFromAddressText(addressText) {
  if (!addressText || typeof addressText !== "string") return null;
  const parts = addressText
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  for (let i = parts.length - 1; i >= 0; i -= 1) {
    const code = normalizeProvinceToken(parts[i]);
    if (code) return code;
  }
  return null;
}

/**
 * @param {string|null|undefined} corridor
 * @returns {string[]}
 */
export function parseCorridorToProvinces(corridor) {
  if (!corridor || typeof corridor !== "string") return [];

  const value = corridor.trim();
  if (!value) return [];

  if (value.includes("-") && !value.includes("_")) {
    return value
      .split("-")
      .map((part) => normalizeProvinceToken(part))
      .filter(Boolean);
  }

  if (value.includes("_")) {
    return value
      .split("_")
      .map((part) => normalizeProvinceToken(part))
      .filter(Boolean);
  }

  const single = normalizeProvinceToken(value);
  return single ? [single] : [];
}

/**
 * @param {string|null|undefined} corridor
 * @returns {string}
 */
export function corridorsMatch(left, right) {
  if (!left || !right) return false;
  if (left === right) return true;

  const leftProvinces = parseCorridorToProvinces(left);
  const rightProvinces = parseCorridorToProvinces(right);

  if (leftProvinces.length === 0 || rightProvinces.length === 0) {
    return left.toLowerCase() === right.toLowerCase();
  }

  if (leftProvinces.length !== rightProvinces.length) {
    return false;
  }

  if (leftProvinces.length === 2) {
    const leftSet = new Set(leftProvinces);
    const rightSet = new Set(rightProvinces);
    return (
      leftSet.size === rightSet.size &&
      [...leftSet].every((province) => rightSet.has(province))
    );
  }

  return (
    leftProvinces.join("-") === rightProvinces.join("-") ||
    leftProvinces.join("-") === [...rightProvinces].reverse().join("-")
  );
}

/**
 * @param {string|null|undefined} corridor
 * @returns {string}
 */
export function formatCorridorDisplay(corridor) {
  const provinces = parseCorridorToProvinces(corridor);
  if (provinces.length >= 2) {
    return provinces.join("-");
  }
  return corridor || "";
}

/**
 * Resolve an order/lead corridor value to a registry corridor name.
 * @param {string|null|undefined} corridor
 * @param {string[]} registryNames
 * @returns {string}
 */
export function resolveRegistryCorridor(corridor, registryNames = []) {
  if (!corridor) return "";

  const display = formatCorridorDisplay(corridor);
  if (registryNames.includes(display)) return display;
  if (registryNames.includes(corridor)) return corridor;

  const matched = registryNames.find((name) => corridorsMatch(corridor, name));
  return matched || display || corridor;
}
